import {
  CalendarPlus,
  ClipboardList,
  Mail,
  MessageSquare,
  Phone,
  Sparkles,
  StickyNote,
} from "lucide-react";
import { useState } from "react";
import { useCreate, useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { invokeAi } from "../ai/invokeAi";
import { type MessageTemplate, renderTemplate } from "./timelineModel";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

type Action = "text" | "email" | "note" | "task" | "schedule" | null;

export interface ActionBarContact {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  do_not_contact?: boolean | null;
  sms_consent?: string | null;
  email_consent?: string | null;
}

/**
 * The six things a service business actually does to a customer record:
 * Call · Text · Email · Note · Task · Schedule.
 *
 * Every outbound action goes through the same consent-gated server RPCs the
 * rest of Gravun uses, so a do-not-contact customer cannot be reached from
 * here either, and the refusal is shown, never swallowed.
 */
export const RecordActionBar = ({
  contact,
  className,
}: {
  contact: ActionBarContact;
  className?: string;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState<Action>(null);
  const [calling, setCalling] = useState(false);

  const blocked = Boolean(contact.do_not_contact);

  const placeCall = async (kind: "human" | "ai") => {
    setCalling(true);
    const { data, error } = await getSupabaseClient().rpc("start_call", {
      p_contact_id: Number(contact.id),
      p_kind: kind,
    });
    setCalling(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    const call = (Array.isArray(data) ? data[0] : data) as {
      status: string;
      error: string | null;
    };
    notify(
      call.status === "queued"
        ? kind === "ai"
          ? "AI call starting, it will appear in the call history"
          : "Call starting, it will appear in the call history"
        : (call.error ?? "The call could not be placed"),
      { type: call.status === "queued" ? "info" : "warning" },
    );
    refresh();
  };

  const actions: {
    key: Action | "call" | "ai";
    label: string;
    icon: typeof Phone;
    onClick: () => void;
    disabled?: boolean;
  }[] = [
    {
      key: "call",
      label: "Call",
      icon: Phone,
      onClick: () => placeCall("human"),
      disabled: calling || blocked,
    },
    {
      key: "text",
      label: "Text",
      icon: MessageSquare,
      onClick: () => setOpen("text"),
      disabled: blocked,
    },
    {
      key: "email",
      label: "Email",
      icon: Mail,
      onClick: () => setOpen("email"),
      disabled: blocked,
    },
    {
      key: "note",
      label: "Note",
      icon: StickyNote,
      onClick: () => setOpen("note"),
    },
    {
      key: "task",
      label: "Task",
      icon: ClipboardList,
      onClick: () => setOpen("task"),
    },
    {
      key: "schedule",
      label: "Schedule",
      icon: CalendarPlus,
      onClick: () => setOpen("schedule"),
    },
    {
      key: "ai",
      label: "AI call",
      icon: Sparkles,
      onClick: () => placeCall("ai"),
      disabled: calling || blocked,
    },
  ];

  return (
    <>
      <div
        className={cn(
          // Horizontally scrollable on phones so all actions stay reachable
          // without wrapping into a second row.
          "-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1",
          className,
        )}
      >
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Button
              key={action.key}
              size="sm"
              variant="outline"
              disabled={action.disabled}
              onClick={action.onClick}
              className="h-9 shrink-0 gap-1.5 px-3"
            >
              <Icon className="size-3.5" />
              {action.label}
            </Button>
          );
        })}
      </div>
      {blocked ? (
        <p className="mt-1 text-[12px] text-amber-700">
          This contact is marked do-not-contact, outbound actions are disabled.
        </p>
      ) : null}

      <ComposeDialog
        action={open}
        contact={contact}
        onClose={() => setOpen(null)}
      />
    </>
  );
};

const ComposeDialog = ({
  action,
  contact,
  onClose,
}: {
  action: Action;
  contact: ActionBarContact;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);

  /**
   * AI draft straight into THIS composer: the text lands in the same body
   * state a hand-typed message uses, so review, edit, consent gating and
   * sending are identical. The AI cannot skip any of it.
   */
  const draftWithAi = async () => {
    setDrafting(true);
    const { data, error } = await invokeAi<{
      text: string;
      provider: string;
    }>({
      action: "draft_followup",
      kind: "contact",
      id: contact.id,
      workspace_id: getActiveWorkspaceId(),
      channel: action === "email" ? "email" : "sms",
    });
    setDrafting(false);
    if (error || !data) {
      notify(error ?? "AI drafting is not available", { type: "warning" });
      return;
    }
    const result = data;
    setBody(result.text);
    if (result.provider === "sandbox") {
      notify("Sandbox draft inserted (no model was called)");
    }
  };

  const channel = action === "email" ? "email" : "sms";
  const { data: templates } = useGetList<MessageTemplate>(
    "message_templates",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
      filter: { channel },
    },
    { enabled: action === "text" || action === "email" },
  );

  const reset = () => {
    setBody("");
    setSubject("");
    setSendAt("");
    onClose();
  };

  const applyTemplate = (templateId: string) => {
    const template = templates?.find((t) => t.id === templateId);
    if (!template) return;
    setBody(renderTemplate(template.body, contact));
    if (template.subject) setSubject(renderTemplate(template.subject, contact));
  };

  /** Finds or creates the thread, then composes through the consent gate. */
  const sendMessage = async () => {
    if (!body.trim()) {
      notify("Write a message first", { type: "warning" });
      return;
    }
    setBusy(true);
    const client = getSupabaseClient();

    const { data: existing } = await client
      .from("conversations")
      .select("id")
      .eq("contact_id", Number(contact.id))
      .eq("channel", channel)
      .order("last_message_at", { ascending: false })
      .limit(1);

    let conversationId = existing?.[0]?.id as string | undefined;
    if (!conversationId) {
      const { data: created, error: createError } = await client
        .from("conversations")
        .insert({
          contact_id: Number(contact.id),
          channel,
          workspace_id: getActiveWorkspaceId(),
        })
        .select("id")
        .single();
      if (createError) {
        setBusy(false);
        notify(createError.message, { type: "error" });
        return;
      }
      conversationId = created.id as string;
    }

    const scheduled = action === "schedule" || Boolean(sendAt);
    const { data, error } = scheduled
      ? await client.rpc("schedule_message", {
          p_conversation_id: conversationId,
          p_body: body,
          p_send_at: new Date(sendAt).toISOString(),
          p_subject: subject || null,
        })
      : await client.rpc("compose_message", {
          p_conversation_id: conversationId,
          p_body: body,
          p_subject: subject || null,
        });

    setBusy(false);
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    const message = (Array.isArray(data) ? data[0] : data) as {
      status: string;
      error: string | null;
    };
    notify(
      message.status === "failed"
        ? (message.error ?? "The message could not be sent")
        : message.status === "scheduled"
          ? "Message scheduled"
          : "Message queued for delivery",
      { type: message.status === "failed" ? "warning" : "info" },
    );
    refresh();
    reset();
  };

  const addNote = () => {
    if (!body.trim()) {
      notify("Write the note first", { type: "warning" });
      return;
    }
    setBusy(true);
    create(
      "contact_notes",
      {
        data: {
          contact_id: Number(contact.id),
          text: body,
          date: new Date().toISOString(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Note added");
          setBusy(false);
          refresh();
          reset();
        },
        onError: (error: unknown) => {
          setBusy(false);
          notify(error instanceof Error ? error.message : "Could not save", {
            type: "error",
          });
        },
      },
    );
  };

  const addTask = () => {
    if (!body.trim()) {
      notify("Describe the task first", { type: "warning" });
      return;
    }
    setBusy(true);
    create(
      "tasks",
      {
        data: {
          contact_id: Number(contact.id),
          text: body,
          type: "Follow-up",
          priority,
          due_date: new Date(`${dueDate}T12:00:00`).toISOString(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Task created");
          setBusy(false);
          refresh();
          reset();
        },
        onError: (error: unknown) => {
          setBusy(false);
          notify(error instanceof Error ? error.message : "Could not save", {
            type: "error",
          });
        },
      },
    );
  };

  const titles: Record<string, string> = {
    text: "Send a text",
    email: "Send an email",
    note: "Add a note",
    task: "Create a task",
    schedule: "Schedule a message",
  };

  const isMessage =
    action === "text" || action === "email" || action === "schedule";

  return (
    <Dialog open={action !== null} onOpenChange={(next) => !next && reset()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{action ? titles[action] : ""}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {isMessage && (templates ?? []).length > 0 && (
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Template
              <select
                className={inputClass}
                defaultValue=""
                onChange={(e) => applyTemplate(e.target.value)}
              >
                <option value="">Start from scratch…</option>
                {(templates ?? []).map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          {action === "email" && (
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Subject
              <input
                className={inputClass}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>
          )}

          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            {action === "task"
              ? "Task"
              : action === "note"
                ? "Note"
                : "Message"}
            <textarea
              className={cn(inputClass, "h-28 resize-y py-2")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={
                action === "task"
                  ? "Call back about the estimate"
                  : `Hi ${contact.first_name ?? "there"}, …`
              }
            />
            {action === "text" ||
            action === "email" ||
            action === "schedule" ? (
              <button
                type="button"
                disabled={drafting}
                onClick={draftWithAi}
                className="flex w-fit items-center gap-1 text-[11.5px] font-normal text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
              >
                <Sparkles className="size-3" />
                {drafting
                  ? "Drafting…"
                  : "Draft with AI (you review before sending)"}
              </button>
            ) : null}
          </label>

          {action === "task" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-[12.5px] font-medium">
                Due
                <input
                  type="date"
                  className={inputClass}
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-[12.5px] font-medium">
                Priority
                <select
                  className={inputClass}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
            </div>
          )}

          {(action === "schedule" ||
            action === "text" ||
            action === "email") && (
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              {action === "schedule" ? "Send at" : "Send later (optional)"}
              <input
                type="datetime-local"
                className={inputClass}
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
              />
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={reset}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={
                action === "note"
                  ? addNote
                  : action === "task"
                    ? addTask
                    : sendMessage
              }
            >
              {busy
                ? "Saving…"
                : action === "note"
                  ? "Add note"
                  : action === "task"
                    ? "Create task"
                    : sendAt || action === "schedule"
                      ? "Schedule"
                      : "Send"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
